<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 700));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "radial-gradient(ellipse at center, #1e1b4b 0%, #050314 80%)",
      fontFamily:
        props.block.fontFamily || "'Space Grotesk', 'Orbitron', sans-serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.4}px`,
    fontSize: `${base}px`,
    radius: `${props.block.cardRadius ?? 18}px`,
  };
});

const stars = Array.from({ length: 50 }, (_, i) => i);

function starStyle(i: number): Record<string, string> {
  const x = ((i * 73) % 100) + (i % 7);
  const y = ((i * 31) % 100) + (i % 5);
  const size = 1 + ((i * 13) % 3);
  const delay = (i * 0.18) % 4;
  return {
    left: `${x}%`,
    top: `${y}%`,
    width: `${size}px`,
    height: `${size}px`,
    animationDelay: `${delay}s`,
  };
}
</script>

<template>
  <div class="cd-galaxy" :style="containerStyle">
    <div class="cd-galaxy__nebula" aria-hidden="true" />
    <div class="cd-galaxy__stars" aria-hidden="true">
      <span
        v-for="i in stars"
        :key="i"
        class="cd-galaxy__star"
        :style="starStyle(i)"
      />
    </div>

    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-galaxy__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 110}ms`,
      }"
    >
      <span
        class="cd-galaxy__digit"
        :key="seg.value"
        :style="{
          fontSize: cardSize.fontSize,
          color: block.digitColor || '#ffffff',
        }"
      >
        {{ seg.value }}
      </span>
      <span
        class="cd-galaxy__label"
        :style="{
          fontSize: `${block.labelFontSize}px`,
          color: block.labelColor || 'rgba(196, 181, 253, 0.85)',
        }"
      >
        {{ seg.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-galaxy {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  overflow: hidden;
  isolation: isolate;
}
.cd-galaxy__nebula {
  position: absolute;
  inset: -25%;
  background:
    radial-gradient(
      circle at 20% 30%,
      rgba(167, 139, 250, 0.45),
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(244, 114, 182, 0.35),
      transparent 55%
    ),
    radial-gradient(
      circle at 60% 30%,
      rgba(96, 165, 250, 0.25),
      transparent 50%
    );
  filter: blur(40px);
  animation: cd-galaxy-drift 30s ease-in-out infinite;
  pointer-events: none;
}
@keyframes cd-galaxy-drift {
  0%,
  100% {
    transform: translate(0, 0) rotate(0);
  }
  50% {
    transform: translate(20px, -10px) rotate(8deg);
  }
}
.cd-galaxy__stars {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.cd-galaxy__star {
  position: absolute;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(255, 255, 255, 0.85);
  animation: cd-galaxy-twinkle 3s ease-in-out infinite;
}
@keyframes cd-galaxy-twinkle {
  0%,
  100% {
    opacity: 0.25;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.4);
  }
}
.cd-galaxy__cell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(15, 10, 40, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(167, 139, 250, 0.35);
  box-shadow:
    0 0 30px rgba(124, 58, 237, 0.35),
    inset 0 0 20px rgba(196, 181, 253, 0.1);
  animation: cd-galaxy-rise 800ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
}
@keyframes cd-galaxy-rise {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.cd-galaxy__digit {
  font-weight: 700;
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
  text-shadow:
    0 0 12px rgba(196, 181, 253, 0.85),
    0 0 28px rgba(124, 58, 237, 0.55);
  display: inline-block;
  animation: cd-galaxy-tick var(--cd-tick, 700ms) cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes cd-galaxy-tick {
  0% {
    opacity: 0;
    transform: scale(1.2) rotate(-3deg);
    filter: blur(4px);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0);
    filter: blur(0);
  }
}
.cd-galaxy__label {
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.28em;
  font-weight: 500;
}
</style>
